import { agentTextSource } from './text';
import type { AnyOfPermission, Permission, PermissionRule, ScreenAction, ScreenManifest } from './types';

export function can(
  resource: string,
  action: string,
  options: { strict?: boolean } = {},
): Permission {
  return { resource, action, ...options };
}

/** "Qualquer uma de N" (`permission: [...]` do legado), com `strict` por item. */
export function anyOf(...permissions: readonly Permission[]): AnyOfPermission {
  return { anyOf: permissions };
}

export function isAnyOf(rule: PermissionRule): rule is AnyOfPermission {
  return 'anyOf' in rule;
}

/** Avalia a regra com o `check` de quem vê; sem regra, libera. `anyOf` vazio nega (`[].some()`). */
export function allows(rule: PermissionRule | undefined, check: (permission: Permission) => boolean): boolean {
  if (!rule) return true;
  return isAnyOf(rule) ? rule.anyOf.some(check) : check(rule);
}

export function action<Input = Record<string, never>>(
  definition: ScreenAction<Input>,
): ScreenAction<Input> {
  return definition;
}

export function defineScreen(manifest: ScreenManifest): ScreenManifest {
  if (import.meta.env.DEV) {
    const issues = validateManifest(manifest);
    if (issues.length > 0) throw new Error(`manifesto ${manifest.id}: ${issues.join('; ')}`);
  }
  return manifest;
}

const ID = /^[a-z][a-zA-Z0-9]*(\.[a-z][a-zA-Z0-9]*)+$/;

export function validateManifest(manifest: ScreenManifest): string[] {
  const issues: string[] = [];
  if (!/^[a-z][a-zA-Z0-9]*(\.[a-z][a-zA-Z0-9]*)*$/.test(manifest.id))
    issues.push(`id de tela inválido: ${manifest.id}`);
  if (!manifest.path.startsWith('/')) issues.push('path precisa começar com /');
  if (agentTextSource(manifest.description).trim().length < 12)
    issues.push('description curta demais para um modelo escolher');
  const anyOfList = manifest.access?.anyOf;
  if (anyOfList?.length === 0) issues.push('access.anyOf vazio nega todo mundo; omita ou liste permissões');
  const place = manifest.place;
  if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(place.icon))
    issues.push(`ícone inválido no place: ${place.icon}`);
  if (place.section && place.parent)
    issues.push('place: section só na raiz; tela com parent herda a seção');
  if (!place.section && !place.parent && manifest.path !== '/')
    issues.push('place: tela sem section nem parent não entra no breadcrumb (U42)');
  if (place.parent === manifest.id) issues.push('place: a tela não pode ser mãe de si mesma');
  const tabIds = new Set<string>();
  for (const tab of place.tabs ?? []) {
    if (tabIds.has(tab.id)) issues.push(`aba duplicada: ${tab.id}`);
    tabIds.add(tab.id);
  }
  const seen = new Set<string>();
  for (const a of manifest.actions) {
    if (!ID.test(a.id)) issues.push(`id de ação inválido: ${a.id}`);
    if (!a.id.startsWith(`${manifest.id.split('.')[0] ?? ''}.`))
      issues.push(`ação ${a.id} fora do domínio da tela`);
    if (seen.has(a.id)) issues.push(`ação duplicada: ${a.id}`);
    seen.add(a.id);
    if (agentTextSource(a.description).trim().length < 12) issues.push(`ação ${a.id} sem descrição útil`);
    if (a.page === true && a.input === undefined)
      issues.push(`ação ${a.id} tem page: true e não tem input (08 §4.2)`);
    const stepUp: unknown = a.stepUp;
    if (stepUp !== undefined && a.kind !== 'write' && a.kind !== 'destructive')
      issues.push(`ação ${a.id}: stepUp só em ação com efeito`);
    if (stepUp !== undefined && a.page === true)
      issues.push(`ação ${a.id}: step-up não entra em página personalizada nem bloco de catálogo (U24, 08 §5)`);
    const preview: unknown = a.preview;
    if ((a.kind === 'write' || a.kind === 'destructive') && typeof preview !== 'function') {
      issues.push(`ação ${a.id} tem efeito e não tem preview (U24)`);
    }
  }
  return issues;
}

export function toolName(actionId: string): string {
  return actionId.replaceAll('.', '_');
}
